import { all, call, put, takeLatest, race, take, cancelled } from "redux-saga/effects";
import { APPEALS_TOP_OVERTURN_REASON_DATA_REQUEST, APPEAL_DETAILS_DATA_REQUEST, APPEALS_TREND_DATA_REQUEST, APPEAL_DETAILS_DATA_CANCEL, APPEALS_TOP_OVERTURN_REASON_DATA_CANCEL, APPEALS_TREND_DATA_CANCEL } from "../../../actions/actionTypes";
import { isLoading } from "../../../actions/SiteOfServiceAction"
// Import ALL actions from action loader to ensure webpack bundles all actions
import * as appealActions from "@src/middleware/actions/Reimbursement/appealActions";

// Extract the specific actions needed for this saga
const {
  fetchAppealTopOverturnDataError,
  fetchAppealTopOverturnDataSuccess,
  fetchAppealTopOverturnDataLoading,
  fetchAppealTrendDataLoading,
  fetchAppealTrendDataSuccess,
  fetchAppealTredDataError
} = appealActions;
import { getAppealTopReasonData, getAppealTopReasonDataBulk, getAppealData, getAppealDataBulk, getAppealTredData, getAppealTrendDataBulk } from "@src/middleware/services/reimbusement/appeal/appeal.service";
import { reimbursementAppealsStateKeys } from "@src/CommonComponents/constant/constant";
import { setRequestError, setRequestLoading, setRequestSuccess } from "@src/middleware/actions/common/common.actions";

import { Payload } from './types';
import { reimbursementPollingCompleted, reimbursementPollingStarted } from "@src/middleware/actions/Reimbursement/reiumbursementTabDataAction";
import { checkAppealsBulkFlow } from "@src/utility/createStorage";


const getServiceMethod = (key: string, highVolumeTinFlag: boolean) => {
  switch (key) {
    case reimbursementAppealsStateKeys.appealsDetailsData:
      return highVolumeTinFlag ? getAppealDataBulk : getAppealData;
    default:
      return highVolumeTinFlag ? getAppealDataBulk : getAppealData;
  }
}

export function* fetchAppealData(action: any) {
  const { key, payload } = action;
  const loadingKey = `${key}Loading`;
  const errorKey = `${key}Error`;
  const highVolumeTinFlag = checkAppealsBulkFlow();
  let stopPollingFn: (() => void) | undefined;
  const correlationId = `getReconPendingData-${Date.now()}`;
  try {
    yield put(setRequestError(false, errorKey));
    yield put(setRequestLoading(true, loadingKey));
    if (highVolumeTinFlag) {
      yield put(reimbursementPollingStarted(correlationId));
    }
    // Race between API call and cancellation
    const { result, cancel } = yield race({
      result: call(function* () {
        const response = yield call(
          getServiceMethod(key, highVolumeTinFlag),
          payload,
          {
            onStop: (stopFn: () => void) => {
              stopPollingFn = stopFn;
            },
          }
        );
        return response;
      }),
      cancel: take(APPEAL_DETAILS_DATA_CANCEL),
    });

    if (cancel) {
      // Cancellation was triggered
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(setRequestLoading(false, loadingKey));
      if (highVolumeTinFlag) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
      return;
    }

    const response = result;
    yield put(setRequestLoading(false, loadingKey));

    if (response?.status === false || response?.Status === 'FAILED') {
      yield put(setRequestError(true, errorKey));
    } else {
      // For bulk API: response is the data object directly - we need to wrap it in array
      const dataToStore = response?.Data ? response.Data : [response];
      if (highVolumeTinFlag) {
        yield put(setRequestSuccess(dataToStore, key));
        yield put(reimbursementPollingCompleted(correlationId));
      } else {
        yield put(setRequestSuccess(dataToStore, key));
      }
    }
  } catch (error) {
    yield put(setRequestLoading(false, loadingKey));
    yield put(setRequestError(true, errorKey));
    if (highVolumeTinFlag) {
      yield put(reimbursementPollingCompleted(correlationId));
    }
  } finally {
    if (yield cancelled()) {
      // Saga was cancelled externally
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(setRequestLoading(false, loadingKey));
      if (highVolumeTinFlag) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }

  }
}

export function* fetchAppealTopReasonData(action: any) {
  const highVolumeTinFlag = checkAppealsBulkFlow();
  let stopPollingFn: (() => void) | undefined;
  const correlationId = `fetchAppealTopReasonData-${Date.now()}`;
  try {
    yield put(fetchAppealTopOverturnDataLoading(true));
    if (highVolumeTinFlag) {
      yield put(reimbursementPollingStarted(correlationId));
    }
    // Race between API call and cancellation
    const { result, cancel } = yield race({
      result: call(function* () {
        const response = yield call(
          highVolumeTinFlag ? getAppealTopReasonDataBulk : getAppealTopReasonData,
          action?.payload,
          {
            onStop: (stopFn: () => void) => {
              stopPollingFn = stopFn;
            },
          }
        );
        return response;
      }),
      cancel: take(APPEALS_TOP_OVERTURN_REASON_DATA_CANCEL),
    });

    if (cancel) {
      // Cancellation was triggered
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchAppealTopOverturnDataLoading(false));
      if (highVolumeTinFlag) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
      return;
    }

    const initialResponse = result;
    if (initialResponse) {
      // For bulk API: response is the data object directly { StartDate, AppealOverTurnReasons, EndDate }
      const dataToStore = initialResponse?.Data ? initialResponse.Data[0] : initialResponse;
      yield put(fetchAppealTopOverturnDataSuccess(dataToStore || {}));
    }
    if (highVolumeTinFlag) {
      yield put(fetchAppealTopOverturnDataLoading(false));
      yield put(reimbursementPollingCompleted(correlationId));
    } else {
      yield put(fetchAppealTopOverturnDataLoading(false));
    }
  } catch (error) {
    yield put(fetchAppealTopOverturnDataError(error));
    yield put(fetchAppealTopOverturnDataLoading(false));
    if (highVolumeTinFlag) {
      yield put(reimbursementPollingCompleted(correlationId));
    }
  } finally {
    if (yield cancelled()) {
      // Saga was cancelled externally
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchAppealTopOverturnDataLoading(false));
      if (highVolumeTinFlag) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }

  }
}



export function* fetchAppealTrendGraphData(action: any) {
  const highVolumeTinFlag = checkAppealsBulkFlow();
  const stopPollingFns: Array<(() => void) | undefined> = [];
  const correlationId = `fetchAppealTrendGraphData-${Date.now()}`;
  try {
    yield put(fetchAppealTrendDataLoading(true));
    if (highVolumeTinFlag) {
      yield put(reimbursementPollingStarted(correlationId));
    }
    const LobData: { Lob: string }[] = [
      { Lob: "All" },
      { Lob: "EI" },
      { Lob: "MR" },
      { Lob: "CS" },
      { Lob: "EXC" }
    ];

    // Race between API calls and cancellation
    const { result, cancel } = yield race({
      result: call(function* () {
        const allResponses: any[] = [];

        for (const lobItem of LobData) {
          const payloadWithLob: Payload = {
            ...action.payload,
            Lob: lobItem.Lob
          };

          let stopPollingFn: (() => void) | undefined;

          const response: Awaited<ReturnType<typeof getAppealTredData>> = yield call(
            highVolumeTinFlag ? getAppealTrendDataBulk : getAppealTredData,
            payloadWithLob,
            {
              onStop: (stopFn: () => void) => {
                stopPollingFn = stopFn;
                stopPollingFns.push(stopFn);
              },
            }
          );

          if (response) {
            let lobResponse;

            if (response?.Data) {
                if (Array.isArray(response.Data) && Array.isArray(response.Data[0])) {
                lobResponse = {
                  Lob: lobItem.Lob,
                  Data: response.Data[0]
                };
              } else {
                lobResponse = {
                  Lob: lobItem.Lob,
                  Data: response.Data                };
              }
            } else if (response?.StartDate && response?.Trend) {
              lobResponse = {
                Lob: lobItem.Lob,
                Data: [response],
                Status: 'OK'
              };
            } else {
              lobResponse = {
                Lob: lobItem.Lob,
                Data: response
              };
            }

            allResponses.push(lobResponse);
          }
        }

        return allResponses;
      }),
      cancel: take(APPEALS_TREND_DATA_CANCEL),
    });

    if (cancel) {
      // Cancellation was triggered - stop all polling operations
      stopPollingFns.forEach(stopFn => {
        if (typeof stopFn === 'function') {
          stopFn();
        }
      });
      yield put(fetchAppealTrendDataLoading(false));
      return;
    }

    const allResponses = result;
    yield put(fetchAppealTrendDataSuccess(allResponses));
  } catch (error) {
    yield put(fetchAppealTredDataError(error));
  } finally {


    if (yield cancelled()) {
      stopPollingFns.forEach(stopFn => {
        if (typeof stopFn === 'function') {
          stopFn();
        }
      });
    }
    if (highVolumeTinFlag) {
      yield put(reimbursementPollingCompleted(correlationId));
      yield put(fetchAppealTrendDataLoading(false));
    } else {
      yield put(fetchAppealTrendDataLoading(false));
    }
  }
}


export function* appealSaga() {
   yield takeLatest(APPEALS_TOP_OVERTURN_REASON_DATA_REQUEST, fetchAppealTopReasonData);
   yield takeLatest(APPEAL_DETAILS_DATA_REQUEST, fetchAppealData);
   yield takeLatest(APPEALS_TREND_DATA_REQUEST, fetchAppealTrendGraphData);
}